import { makeObservable, observable, runInAction, action, computed } from 'mobx'

import { createCatalog, getCatalogs } from '../firestore/Catalog'
import { Catalog } from '../models/Catalog'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import type { StaticModelCollection } from '../types'
import { Cubit } from './core'
import {
  fetchSlideDeckCountForCatalog,
  fetchViewerCountForCatalog,
} from '../firestore/SlideDeck'
import { fetchTeachingPlanCount } from '../firestore/TeachingPlan'
import { UserProfileFavoriteCatalog } from '../models/UserProfileFavoriteCatalog'
import {
  addUserProfileFavoriteCatalog,
  getUserProfileFavoriteCatalogs,
  removeUserProfileFavoriteCatalog,
} from '../firestore/UserProfileFavoriteCatalog'
import { addCatalogToUserProfile } from '../firestore/UserProfile'
import { fetchUserSearch } from '../firestore/UserSearch'
import type { UserSearch } from '@breakoutlearning/firebase-repository/stores/UserSearch'

export type CatalogTab = 'public' | 'private' | 'favorites'
export const MIN_CHARACTERS_FOR_INSTRUCTOR_SEARCH = 3

export type CatalogSortStrategy =
  | 'catalogName'
  | 'experiences'
  | 'nextbooks'
  | 'viewers'
  | 'lastUpdated'

export class AdminCatalogsCubit extends Cubit {
  repository: FirebaseRepository

  catalogs: StaticModelCollection<Catalog>
  favoriteCatalogs: StaticModelCollection<UserProfileFavoriteCatalog>
  @observable slideDeckCounts = observable.map<string, number>()
  @observable viewerCounts = observable.map<string, number>()
  @observable teachingPlanCounts = observable.map<string, number>()

  @observable favoritingCatalogIds = observable.set<string>()
  @observable showHidden = false
  @observable selectedTab: CatalogTab | undefined = undefined
  @observable sortingStrategy: CatalogSortStrategy = 'catalogName'
  @observable sortingOrder: 'asc' | 'desc' = 'asc'

  private initialFavoritesLoaded = false

  @observable searchedInstructors = observable.array<UserSearch>([])
  @observable instructorSearchLoading = false
  @observable instructorSearchTerm = ''
  @observable searchedInstructorTerms = observable.set<string>()

  constructor(
    repository: FirebaseRepository,
    options: { selectedTab?: CatalogTab } = {}
  ) {
    super()
    makeObservable(this)
    this.repository = repository
    this.catalogs = Catalog.emptyCollection(repository)
    this.favoriteCatalogs =
      UserProfileFavoriteCatalog.emptyCollection(repository)

    if (options.selectedTab) {
      this.selectedTab = options.selectedTab
    }
  }

  @action
  setSelectedTab(tab: CatalogTab): void {
    this.selectedTab = tab
  }

  @action
  changeSortingStrategy = (
    strategy: CatalogSortStrategy,
    order: 'asc' | 'desc'
  ) => {
    this.sortingStrategy = strategy
    this.sortingOrder = order
  }

  @computed
  get publicCatalogsCount(): number {
    return this.catalogs.models.filter(
      (catalog) => catalog.data.isPublic === true
    ).length
  }

  @computed
  get privateCatalogsCount(): number {
    return this.catalogs.models.filter((catalog) => !catalog.data.isPublic)
      .length
  }

  @computed
  get favoritedCatalogsCount(): number {
    return this.favoriteCatalogs.models.length
  }

  @computed
  get filteredCatalogs(): Catalog[] {
    return this.catalogs.models.filter((catalog) => {
      switch (this.selectedTab) {
        case 'public':
          return catalog.data.isPublic === true
        case 'private':
          return !catalog.data.isPublic
        case 'favorites':
          return this.favoriteCatalogs.models.some((c) => c.id === catalog.id)
        default:
          return false
      }
    })
  }

  @computed
  get sortedCatalogs(): Catalog[] {
    const sorted = [...this.filteredCatalogs]
    sorted.sort((a, b) => {
      let compare = 0
      switch (this.sortingStrategy) {
        case 'catalogName':
          compare = a.data.catalogName.localeCompare(b.data.catalogName)
          break
        case 'experiences': {
          const aCount = this.slideDeckCounts.get(a.id) ?? 0
          const bCount = this.slideDeckCounts.get(b.id) ?? 0
          compare = aCount - bCount
          break
        }
        case 'nextbooks': {
          const aCount = this.teachingPlanCounts.get(a.id) ?? 0
          const bCount = this.teachingPlanCounts.get(b.id) ?? 0
          compare = aCount - bCount
          break
        }
        case 'viewers': {
          const aCount = this.viewerCounts.get(a.id) ?? 0
          const bCount = this.viewerCounts.get(b.id) ?? 0
          compare = aCount - bCount
          break
        }
        case 'lastUpdated':
          compare =
            (b.data.updatedAt?.getTime() ?? 0) -
            (a.data.updatedAt?.getTime() ?? 0) // Yes this order is correct
          break
      }
      return this.sortingOrder === 'asc' ? compare : -compare
    })
    return sorted
  }

  initialize(): void {
    if (!this.repository.currentUser) {
      return
    }
    this.addStream(
      getUserProfileFavoriteCatalogs(this.repository, {
        userId: this.repository.currentUser.uid,
      }),
      (favoriteCatalogs) => {
        runInAction(() => {
          if (!this.initialFavoritesLoaded) {
            if (this.selectedTab === undefined) {
              if (favoriteCatalogs.length > 0) {
                this.selectedTab = 'favorites'
              } else {
                this.selectedTab = 'public'
              }
            }
            this.initialFavoritesLoaded = true
          }
          this.favoriteCatalogs.replaceModels(favoriteCatalogs)
        })
      }
    )
    this.addStream(getCatalogs(this.repository), (catalogs) => {
      this.catalogs.replaceModels(catalogs)
    })
  }

  @action
  fetchCountsForCatalogs(catalogs: Catalog[]): void {
    catalogs.forEach((catalog) => {
      this._fetchSlideDeckCount(catalog)
      this._fetchViewerCount(catalog)
      this._fetchTeachingPlanCount(catalog)
    })
  }

  private async _fetchSlideDeckCount(catalog: Catalog) {
    if (this.slideDeckCounts.has(catalog.id)) {
      return
    }
    const count = await fetchSlideDeckCountForCatalog(this.repository, {
      catalogId: catalog.id,
    })
    runInAction(() => {
      this.slideDeckCounts.set(catalog.id, count)
    })
  }

  private async _fetchViewerCount(catalog: Catalog) {
    if (this.viewerCounts.has(catalog.id)) {
      return
    }
    const count = await fetchViewerCountForCatalog(this.repository, {
      catalogId: catalog.id,
    })
    runInAction(() => {
      this.viewerCounts.set(catalog.id, count)
    })
  }

  private async _fetchTeachingPlanCount(catalog: Catalog) {
    if (this.teachingPlanCounts.has(catalog.id)) {
      return
    }
    const count = await fetchTeachingPlanCount(this.repository, {
      catalogId: catalog.id,
    })
    runInAction(() => {
      this.teachingPlanCounts.set(catalog.id, count)
    })
  }

  async addFavoriteCatalog(catalogId: string): Promise<void> {
    const userId = this.repository.currentUser?.uid
    if (!userId) {
      return
    }
    runInAction(() => {
      this.favoritingCatalogIds.add(catalogId)
    })
    try {
      await addUserProfileFavoriteCatalog(this.repository, {
        userId,
        catalogId,
      })
    } finally {
      runInAction(() => {
        this.favoritingCatalogIds.delete(catalogId)
      })
    }
  }

  async removeFavoriteCatalog(catalogId: string): Promise<void> {
    const userId = this.repository.currentUser?.uid
    if (!userId) {
      return
    }
    runInAction(() => {
      this.favoritingCatalogIds.add(catalogId)
    })
    try {
      await removeUserProfileFavoriteCatalog(this.repository, {
        userId,
        catalogId,
      })
    } finally {
      runInAction(() => {
        this.favoritingCatalogIds.delete(catalogId)
      })
    }
  }

  async createCatalog(params: {
    catalogName: string
    catalogDescription: string
    isPublic: boolean
  }): Promise<string> {
    const catalogId = await createCatalog(this.repository, params)
    return catalogId
  }

  async addCatalogToUserProfile(catalogId: string, userId: string) {
    await addCatalogToUserProfile(this.repository, {
      catalogId,
      userId,
    })
  }

  @action
  async searchInstructors(term: string) {
    this.instructorSearchTerm = term
    this.instructorSearchLoading = true
    try {
      if (!term || term.length < MIN_CHARACTERS_FOR_INSTRUCTOR_SEARCH) {
        runInAction(() => {
          this.instructorSearchLoading = false
        })
        return
      }

      const isSubsetOfPreviousSearch = Array.from(
        this.searchedInstructorTerms
      ).some((searchedTerm) => searchedTerm.includes(term))
      if (isSubsetOfPreviousSearch) {
        runInAction(() => {
          this.instructorSearchLoading = false
        })
        return
      }

      const results = await fetchUserSearch(this.repository, 'instructor', term)
      runInAction(() => {
        results.forEach((user) => {
          if (!this.searchedInstructors.find((u) => u.id === user.id)) {
            this.searchedInstructors.push(user)
          }
        })
        this.searchedInstructorTerms.add(term)
        this.instructorSearchLoading = false
      })
    } catch (err) {
      runInAction(() => {
        this.instructorSearchLoading = false
      })
    }
  }
}
